import { useRef, useState } from "react";
import { toast } from "sonner";
import { ImageUp, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

const TYPES = ["image/png", "image/jpeg", "image/webp", "image/svg+xml", "image/x-icon", "image/vnd.microsoft.icon"];
const MAX_BYTES = 400_000;

/**
 * Pick a small image and hand it back as a data URL.
 *
 * Checked here as well as on the server — a file that's too large should be
 * refused before it's read and sent, not after.
 */
export default function ImagePicker({ label, hint, value, onChange, preview = "square" }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const pick = (file) => {
    if (!file) return;
    if (!TYPES.includes(file.type)) {
      toast.error("Use a PNG, JPEG, WebP, SVG or ICO");
      return;
    }
    if (file.size > MAX_BYTES) {
      toast.error(`That file is ${Math.round(file.size / 1000)}KB — keep it under 400KB`);
      return;
    }
    setBusy(true);
    const reader = new FileReader();
    reader.onload = () => {
      onChange(String(reader.result));
      setBusy(false);
    };
    reader.onerror = () => {
      toast.error("Couldn't read that file");
      setBusy(false);
    };
    reader.readAsDataURL(file);
  };

  return (
    <div className="space-y-2">
      <div className="text-[13px] font-medium text-ink">{label}</div>
      {hint && <p className="text-[12px] text-muted2 leading-relaxed">{hint}</p>}

      <div className="flex items-center gap-3">
        <div
          className={cn(
            "shrink-0 border border-line bg-surface flex items-center justify-center overflow-hidden",
            preview === "round" ? "w-12 h-12 rounded-full" : "w-12 h-12 rounded-xl"
          )}
        >
          {value ? (
            <img src={value} alt="" className="w-full h-full object-contain" />
          ) : (
            <ImageUp className="w-5 h-5 text-muted2" />
          )}
        </div>

        <input
          ref={inputRef}
          type="file"
          accept={TYPES.join(",")}
          className="hidden"
          onChange={(e) => {
            pick(e.target.files?.[0]);
            e.target.value = "";
          }}
        />
        <Button variant="outline" size="sm" disabled={busy} onClick={() => inputRef.current?.click()}>
          {value ? "Change" : "Choose a file"}
        </Button>
        {value && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onChange("")}
            className="text-muted2 hover:text-stock-out"
            aria-label={`Remove ${label}`}
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        )}
      </div>
    </div>
  );
}
